import React, { useRef, useEffect, useState } from 'react';
import * as Babel from '@babel/standalone';
import { Button } from '@os/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@os/components/ui/card';
import { Alert, AlertDescription } from '@os/components/ui/alert';
import { ScrollArea } from '@os/components/ui/scroll-area';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@os/components/ui/resizable';
import { Play, Square, RefreshCw, AlertTriangle, Terminal } from 'lucide-react';
import { useToast } from '@os/hooks/use-toast';

interface ReactRunnerProps {
  code: string;
  language: string;
}

const ReactRunner: React.FC<ReactRunnerProps> = ({ code, language }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<Array<{ type: 'log' | 'error' | 'warn', message: string }>>([]);
  const { toast } = useToast();

  const clearLogs = () => setLogs([]);

  const transpileCode = (sourceCode: string) => {
    try {
      // Handle different file types
      let presets = ['react'];
      
      if (language === 'typescript' || language === 'tsx') {
        presets.push('typescript');
      }

      const result = Babel.transform(sourceCode, {
        presets: presets,
        plugins: [
          ['transform-modules-umd', { globals: { react: 'React', 'react-dom': 'ReactDOM' } }]
        ]
      });

      return result.code;
    } catch (error: any) {
      throw new Error(`Transpilation failed: ${error.message}`);
    }
  };

  const createHTMLTemplate = (compiledCode: string) => {
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>React App Preview</title>
  <script crossorigin src="https://unpkg.com/react@18/umd/react.development.js"></script>
  <script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"></script>
  <style>
    body {
      margin: 0;
      padding: 20px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
        'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
        sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      background: white;
      color: #333;
    }
    
    #root {
      min-height: 100vh;
    }
    
    .error {
      background: #ffebee;
      color: #c62828;
      padding: 16px;
      border-radius: 4px;
      border-left: 4px solid #f44336;
      margin: 10px 0;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    }
  </style>
</head>
<body>
  <div id="root"></div>
  
  <script>
    // Capture console logs and send to parent
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;
    
    console.log = (...args) => {
      originalLog(...args);
      window.parent.postMessage({
        type: 'console',
        level: 'log',
        message: args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' ')
      }, '*');
    };
    
    console.error = (...args) => {
      originalError(...args);
      window.parent.postMessage({
        type: 'console',
        level: 'error',
        message: args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' ')
      }, '*');
    };
    
    console.warn = (...args) => {
      originalWarn(...args);
      window.parent.postMessage({
        type: 'console',
        level: 'warn',
        message: args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' ')
      }, '*');
    };
    
    // Global error handler
    window.addEventListener('error', (event) => {
      console.error('Runtime Error:', event.error?.message || event.message);
      document.getElementById('root').innerHTML = 
        '<div class="error"><strong>Runtime Error:</strong><br>' + 
        (event.error?.message || event.message) + '</div>';
    });
    
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled Promise Rejection:', event.reason);
      document.getElementById('root').innerHTML = 
        '<div class="error"><strong>Promise Rejection:</strong><br>' + 
        String(event.reason) + '</div>';
    });
    
    // Execute the compiled code
    try {
      const module = { exports: {} };
      const exports = module.exports;
      
      // Execute the transpiled code
      ${compiledCode}
      
      // Try to find and render the component
      let Component = null;
      
      // Look for exported component in various ways
      if (module.exports.default) {
        Component = module.exports.default;
      } else if (module.exports.App) {
        Component = module.exports.App;
      } else if (window.App) {
        Component = window.App;
      } else {
        // Try to find any function that looks like a component
        for (const key in module.exports) {
          if (typeof module.exports[key] === 'function') {
            Component = module.exports[key];
            break;
          }
        }
      }
      
      if (Component) {
        const root = ReactDOM.createRoot(document.getElementById('root'));
        root.render(React.createElement(Component));
        console.log('✅ App rendered successfully!');
      } else {
        throw new Error('No React component found. Make sure to export a component as default or named "App".');
      }
      
    } catch (error) {
      console.error('Execution Error:', error.message);
      document.getElementById('root').innerHTML = 
        '<div class="error"><strong>Execution Error:</strong><br>' + 
        error.message + '</div>';
    }
  </script>
</body>
</html>`;
  };

  const runCode = async () => {
    if (!code.trim()) {
      setError('No code to run');
      return;
    }

    // Check if the code contains HTML (not React)
    if (code.trim().startsWith('<!DOCTYPE') || code.trim().startsWith('<html')) {
      setError('Cannot run HTML files. Please open a React component (.js, .jsx, .ts, .tsx) file instead.');
      return;
    }

    setIsRunning(true);
    setError(null);
    clearLogs();

    try {
      // Transpile the code
      const compiledCode = transpileCode(code);
      
      // Create the HTML with the compiled code
      const html = createHTMLTemplate(compiledCode);
      
      // Load it in the iframe
      if (iframeRef.current) {
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        iframeRef.current.src = url;
        
        // Clean up the blob URL after loading
        iframeRef.current.onload = () => {
          URL.revokeObjectURL(url);
        };
      }
      
      toast({
        title: "Code compiled successfully",
        description: "Your React app is now running!",
      });

    } catch (error: any) {
      setError(error.message);
      toast({
        title: "Compilation failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsRunning(false);
    }
  };

  const stopCode = () => {
    if (iframeRef.current) {
      iframeRef.current.src = 'about:blank';
    }
    clearLogs();
    setError(null);
    toast({
      title: "App stopped",
      description: "The React app has been stopped.",
    });
  };

  const refreshCode = () => {
    runCode();
  };

  // Listen for console messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'console') {
        setLogs(prev => [...prev, {
          type: event.data.level,
          message: event.data.message
        }]);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <div className="h-full flex flex-col">
      {/* Controls */}
      <div className="flex items-center gap-2 p-2 border-b">
        <Button
          onClick={runCode}
          disabled={isRunning}
          size="sm"
          className="flex items-center gap-1"
        >
          <Play className="w-4 h-4" />
          {isRunning ? 'Compiling...' : 'Run'}
        </Button>
        
        <Button
          onClick={stopCode}
          variant="outline"
          size="sm"
          className="flex items-center gap-1"
        >
          <Square className="w-4 h-4" />
          Stop
        </Button>
        
        <Button
          onClick={refreshCode}
          variant="outline"
          size="sm"
          className="flex items-center gap-1"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <Alert variant="destructive" className="m-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Main Content - Split Layout */}
      <div className="flex-1 overflow-hidden">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          {/* Preview Panel */}
          <ResizablePanel defaultSize={70} minSize={30}>
            <div className="h-full flex flex-col">
              <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/50">
                <Play className="w-4 h-4" />
                <span className="text-sm font-medium">Preview</span>
              </div>
              <div className="flex-1 overflow-hidden">
                <iframe
                  ref={iframeRef}
                  className="w-full h-full border-none"
                  title="React App Preview"
                  sandbox="allow-scripts allow-same-origin"
                  src="about:blank"
                />
              </div>
            </div>
          </ResizablePanel>
          
          <ResizableHandle withHandle />
          
          {/* Console Panel */}
          <ResizablePanel defaultSize={30} minSize={20}>
            <div className="h-full flex flex-col">
              <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/50">
                <Terminal className="w-4 h-4" />
                <span className="text-sm font-medium">Console Output</span>
                {logs.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearLogs}
                    className="ml-auto h-6 px-2 text-xs"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <div className="flex-1 overflow-hidden p-2">
                <ScrollArea className="h-full">
                  {logs.length === 0 ? (
                    <div className="text-muted-foreground text-sm p-2">No console output</div>
                  ) : (
                    <div className="space-y-1">
                      {logs.map((log, index) => (
                        <div
                          key={index}
                          className={`text-xs font-mono p-2 rounded ${
                            log.type === 'error'
                              ? 'bg-red-50 text-red-700 border-red-200'
                              : log.type === 'warn'
                              ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                              : 'bg-gray-50 text-gray-700 border-gray-200'
                          } border`}
                        >
                          <span className="text-muted-foreground mr-2">
                            [{log.type.toUpperCase()}]
                          </span>
                          {log.message}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  );
};

export default ReactRunner;